import type { ComponentProps, HTMLAttributes } from "react";
import { useEffect, useRef, useState } from "react";
import { cn } from "../lib/utils";
import { Alert as ShadcnAlert } from "./ui/alert";
import { Badge as ShadcnBadge } from "./ui/badge";
import { Button as ShadcnButton } from "./ui/button";
import {
  Card as ShadcnCard,
  CardContent as ShadcnCardContent,
  CardHeader as ShadcnCardHeader,
} from "./ui/card";
import { Input as ShadcnInput } from "./ui/input";
import { NativeSelect } from "./ui/native-select";
import { Skeleton as ShadcnSkeleton } from "./ui/skeleton";
import { Textarea as ShadcnTextarea } from "./ui/textarea";

export function Button({
  className,
  variant = "primary",
  ...props
}: Omit<ComponentProps<typeof ShadcnButton>, "variant"> & {
  variant?: "primary" | "secondary" | "ghost" | "danger";
}) {
  const shadcnVariant = {
    primary: "default",
    secondary: "outline",
    ghost: "ghost",
    danger: "destructive",
  }[variant] as ComponentProps<typeof ShadcnButton>["variant"];
  return (
    <ShadcnButton
      variant={shadcnVariant}
      className={cn("button", className)}
      nativeButton={!props.render}
      {...props}
    />
  );
}

export function Card({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <ShadcnCard className={cn("card", className)} {...props} />;
}
export function CardHeader({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <ShadcnCardHeader className={cn("card-header", className)} {...props} />
  );
}
export function CardContent({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <ShadcnCardContent className={cn("card-content", className)} {...props} />
  );
}
export function Badge({
  children,
  tone = "neutral",
}: {
  children: React.ReactNode;
  tone?: "neutral" | "good" | "warn" | "bad" | "jam";
}) {
  const variant =
    tone === "bad" ? "destructive" : tone === "jam" ? "default" : "outline";
  return (
    <ShadcnBadge variant={variant} className="badge">
      {children}
    </ShadcnBadge>
  );
}
export function Input(props: ComponentProps<typeof ShadcnInput>) {
  return <ShadcnInput {...props} />;
}
export function Select(props: ComponentProps<typeof NativeSelect>) {
  return <NativeSelect {...props} className={cn("w-full", props.className)} />;
}
export function Textarea(props: ComponentProps<typeof ShadcnTextarea>) {
  return <ShadcnTextarea {...props} />;
}
export function Alert({
  children,
  tone = "info",
}: {
  children: React.ReactNode;
  tone?: "info" | "error" | "success";
}) {
  return (
    <ShadcnAlert
      role={tone === "error" ? "alert" : "status"}
      aria-live={tone === "error" ? "assertive" : "polite"}
      variant={tone === "error" ? "destructive" : "default"}
      className={`alert alert-${tone}`}
    >
      {children}
    </ShadcnAlert>
  );
}
export function Skeleton({ className }: { className?: string }) {
  return (
    <ShadcnSkeleton aria-hidden="true" className={cn("skeleton", className)} />
  );
}

export function Pagination({ children }: { children: React.ReactNode }) {
  return (
    <nav aria-label="Pagination" className="row-actions pagination-actions">
      {children}
    </nav>
  );
}

export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel,
  onOpenChange,
  onConfirm,
  destructive = false,
}: {
  open: boolean;
  title: string;
  description: string;
  confirmLabel: string;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => Promise<void> | void;
  destructive?: boolean;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);
  return (
    <dialog
      ref={ref}
      className="dialog"
      aria-labelledby="confirm-dialog-title"
      onCancel={(event) => {
        event.preventDefault();
        if (!busy) onOpenChange(false);
      }}
      onClose={() => onOpenChange(false)}
    >
      <h2 id="confirm-dialog-title">{title}</h2>
      <p>{description}</p>
      <div className="dialog-actions">
        <Button
          autoFocus
          variant="secondary"
          disabled={busy}
          onClick={() => onOpenChange(false)}
        >
          Cancel
        </Button>
        <Button
          variant={destructive ? "danger" : "primary"}
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            try {
              await onConfirm();
              onOpenChange(false);
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? "Working…" : confirmLabel}
        </Button>
      </div>
    </dialog>
  );
}

export function PromptDialog({
  open,
  title,
  description,
  label,
  defaultValue = "",
  confirmLabel,
  requiredValue,
  onOpenChange,
  onConfirm,
  destructive = false,
}: {
  open: boolean;
  title: string;
  description: string;
  label: string;
  defaultValue?: string;
  confirmLabel: string;
  requiredValue?: string;
  onOpenChange: (open: boolean) => void;
  onConfirm: (value: string) => Promise<void> | void;
  destructive?: boolean;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const [value, setValue] = useState(defaultValue);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      setValue(defaultValue);
      dialog.showModal();
    }
    if (!open && dialog.open) dialog.close();
  }, [defaultValue, open]);
  const valid = requiredValue ? value === requiredValue : value.trim() !== "";
  return (
    <dialog
      ref={ref}
      className="dialog"
      aria-labelledby="prompt-dialog-title"
      onCancel={(event) => {
        event.preventDefault();
        if (!busy) onOpenChange(false);
      }}
      onClose={() => onOpenChange(false)}
    >
      <form
        onSubmit={async (event) => {
          event.preventDefault();
          if (!valid) return;
          setBusy(true);
          try {
            await onConfirm(value.trim());
            onOpenChange(false);
          } finally {
            setBusy(false);
          }
        }}
      >
        <h2 id="prompt-dialog-title">{title}</h2>
        <p>{description}</p>
        <Field label={label}>
          <Input
            autoFocus
            value={value}
            autoComplete="off"
            onChange={(event) => setValue(event.target.value)}
          />
        </Field>
        <div className="dialog-actions">
          <Button
            type="button"
            variant="secondary"
            disabled={busy}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant={destructive ? "danger" : "primary"}
            disabled={busy || !valid}
          >
            {busy ? "Working…" : confirmLabel}
          </Button>
        </div>
      </form>
    </dialog>
  );
}

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      {children}
      {hint && <span className="field-hint">{hint}</span>}
    </label>
  );
}
